import React, { useState, useEffect, forwardRef } from 'react';

/**
 * SafeImage — wrapper de <img> que troca para um fallback quando a URL
 * falha em carregar (404 / 403 / DNS error).
 *
 * Útil enquanto temos URLs órfãs do GCS antigo no banco: o navegador
 * tentaria mostrar o ícone nativo de "imagem quebrada" + alt text.
 * Aqui interceptamos o `onError` e renderizamos o fallback no lugar.
 *
 * Props:
 *   - src        URL a tentar carregar.
 *   - fallback   Pode ser uma string (URL alternativa) OU um nó JSX
 *                (ex: ícone) — exibido quando src falha ou está vazio.
 *   - alt, ...   Demais props são passadas para o <img>.
 *   - ref        Encaminhado para o <img> renderizado (ou para o nó
 *                fallback quando ele é um elemento JSX).
 *
 * Mantém compatibilidade total com a API do <img>: width, height,
 * className, style, loading, onClick, etc.
 */
const SafeImage = forwardRef(({ src, fallback, alt = '', onError, ...rest }, ref) => {
    const [errored, setErrored] = useState(false);

    // Reset do estado quando o src muda (ex: navegar entre itens)
    useEffect(() => {
        setErrored(false);
    }, [src]);

    const isEmpty = !src || src === '';
    const showFallback = isEmpty || errored;

    if (showFallback) {
        // fallback como URL string → renderiza outro <img>
        if (typeof fallback === 'string') {
            return <img ref={ref} src={fallback} alt={alt} {...rest} />;
        }
        // fallback como JSX (ícone/componente) → renderiza direto
        if (React.isValidElement(fallback)) {
            return fallback;
        }
        // sem fallback: renderiza nada (evita o broken icon nativo)
        return null;
    }

    return (
        <img
            ref={ref}
            src={src}
            alt={alt}
            onError={(e) => {
                setErrored(true);
                if (typeof onError === 'function') onError(e);
            }}
            {...rest}
        />
    );
});

SafeImage.displayName = 'SafeImage';

export default SafeImage;
